import { useState } from "react"
import { Link } from "react-router-dom"
import { Sun, Upload, FileAudio, AlertCircle, Download, Mail, ArrowLeft } from "lucide-react"

const SolarEnergyAnalyzer = () => {
  const [file, setFile] = useState(null)
  const [callType, setCallType] = useState("qualification")
  const [agent, setAgent] = useState("")
  const [loading, setLoading] = useState(false)
  const [transcript, setTranscript] = useState("")
  const [analysis, setAnalysis] = useState("")
  const [error, setError] = useState("")

  const callTypes = [
    { value: "qualification", label: "Qualification Call", description: "Initial solar assessment" },
    { value: "site_assessment", label: "Site Assessment", description: "Technical evaluation" },
    { value: "financing", label: "Financing Discussion", description: "Payment options" },
    { value: "follow_up", label: "Follow-up", description: "Re-engagement call" },
  ]

  const handleUpload = async () => {
    if (!file) {
      setError("Please select a file")
      return
    }

    const formData = new FormData()
    formData.append("file", file)
    formData.append("type", callType)
    formData.append("agent", agent)
    // Add industry flag for backend
    formData.append("industry", "solar")

    setLoading(true)
    setError("")

    try {
      const token = localStorage.getItem("token")
      const response = await fetch("https://call-analyzer-backend.onrender.com/analyze", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.detail || "Analysis failed")
      }

      const data = await response.json()
      setTranscript(data.transcript)
      setAnalysis(data.analysis)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white py-6 px-6 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center">
            <Sun className="h-8 w-8 mr-3" />
            <div>
              <h1 className="text-2xl font-bold">Solar & Energy Efficiency Analyzer</h1>
              <p className="text-amber-100">Optimize solar sales and energy audit conversations</p>
            </div>
          </div>
          <Link to="/analyzer" className="flex items-center text-white hover:text-amber-100 transition">
            <ArrowLeft className="h-5 w-5 mr-1" />
            All Analyzers
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Upload Section */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Upload Solar Call</h2>

              {/* Call Type Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Call Type
                </label>
                <select
                  value={callType}
                  onChange={(e) => setCallType(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                >
                  {callTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {callTypes.find(t => t.value === callType)?.description}
                </p>
              </div>

              {/* Agent Name */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Agent Name
                </label>
                <input
                  type="text"
                  value={agent}
                  onChange={(e) => setAgent(e.target.value)}
                  placeholder="Enter agent name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                />
              </div>

              {/* File Upload */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Audio File
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-amber-500 transition">
                  <FileAudio className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <input
                    type="file"
                    accept=".mp3,.wav,.m4a,.mp4,.mpeg,.mpga,.webm,.ogg"
                    onChange={(e) => setFile(e.target.files[0])}
                    className="hidden"
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer text-amber-600 hover:text-amber-700 font-medium"
                  >
                    Choose file
                  </label>
                  {file && (
                    <p className="text-sm text-gray-600 mt-2">{file.name}</p>
                  )}
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
                  <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Upload Button */}
              <button
                onClick={handleUpload}
                disabled={loading || !file}
                className="w-full bg-gradient-to-r from-amber-600 to-orange-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <Upload className="animate-spin h-5 w-5 mr-2" />
                    Analyzing Solar Call...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <Upload className="h-5 w-5 mr-2" />
                    Analyze Call
                  </span>
                )}
              </button>

              {/* Solar-Specific Tips */}
              <div className="mt-6 p-4 bg-amber-50 rounded-lg">
                <h3 className="font-semibold text-amber-900 mb-2">Solar Call Tips</h3>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• Verify roof ownership and condition</li>
                  <li>• Discuss current energy bills</li>
                  <li>• Explain net metering benefits</li>
                  <li>• Address financing options early</li>
                  <li>• Set realistic timeline expectations</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="lg:col-span-2">
            {analysis || transcript ? (
              <div className="space-y-6">
                {/* Analysis */}
                {analysis && (
                  <div className="bg-white rounded-xl shadow-lg p-6">
                    <div className="flex justify-between items-start mb-4">
                      <h2 className="text-xl font-bold text-gray-900">Solar Call Analysis</h2>
                      <div className="flex gap-2">
                        <button className="p-2 text-gray-600 hover:text-amber-600 transition">
                          <Download className="h-5 w-5" />
                        </button>
                        <button className="p-2 text-gray-600 hover:text-amber-600 transition">
                          <Mail className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                    <div className="prose max-w-none">
                      <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
                        {analysis}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Transcript */}
                {transcript && (
                  <div className="bg-white rounded-xl shadow-lg p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-4">Call Transcript</h2>
                    <div className="prose max-w-none">
                      <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
                        {transcript}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                <Sun className="h-16 w-16 text-amber-200 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Ready to Analyze Solar Calls
                </h2>
                <p className="text-gray-600">
                  Upload a solar sales call to receive AI-powered analysis tailored for the solar industry
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SolarEnergyAnalyzer
